<template>
    <div class="space-y-6">
        <div class="flex items-center justify-between">
            <h2 class="text-heading-md">Search Input Examples</h2>
            <div class="flex items-center gap-4">
                <span class="text-content-md font-medium text-content-secondary">Size:</span>
                <DsRadio
                    v-model="size"
                    label="Medium (default)"
                    name="search-input-size"
                    value="md"
                />
                <DsRadio
                    v-model="size"
                    label="Small"
                    name="search-input-size"
                    value="sm"
                />
            </div>
        </div>

        <div class="grid grid-cols-3 gap-x-4 gap-y-8">
            <div>
                <DsSearchCombobox
                    v-model="selectedProduct1"
                    v-model:options="productOptions1"
                    :isSearching="isSearchingProduct1"
                    label="Base"
                    placeholder="Search product"
                    :size
                    @performSearch="handlePerformSearch1"
                />
                <div class="mt-3 text-content-md text-content-secondary">Selected product: {{ selectedProduct1 }}</div>
            </div>
            <div>
                <DsSearchCombobox
                    v-model="selectedProduct2"
                    v-model:options="productOptions2"
                    :defaultOptions
                    :isSearching="isSearchingProduct2"
                    label="With default options"
                    placeholder="Search product"
                    :size
                    @performSearch="handlePerformSearch2"
                />
                <div class="mt-3 text-content-md text-content-secondary">Selected product: {{ selectedProduct2 }}</div>
            </div>
            <div>
                <DsSearchCombobox
                    v-model="selectedProduct3"
                    v-model:options="productOptions3"
                    :initialOptionLabel="selectedProduct3?.title"
                    :isSearching="isSearchingProduct3"
                    label="With initial state"
                    placeholder="Search product"
                    :size
                    @performSearch="handlePerformSearch3"
                />
                <div class="mt-3 text-content-md text-content-secondary">Selected product: {{ selectedProduct3 }}</div>
            </div>
            <div>
                <DsSearchCombobox
                    disabled
                    label="Disabled"
                    :size
                />
            </div>
            <div>
                <DsSearchCombobox
                    label="With error state"
                    searchError="An unexpected error has occurred"
                    :size
                />
            </div>
            <div>
                <DsSearchCombobox
                    label="With warning"
                    placeholder="Search product"
                    :size
                    warning="Warning message"
                />
            </div>
            <div>
                <DsSearchCombobox
                    error="This field is required"
                    label="With error"
                    placeholder="Search product"
                    required
                    :size
                />
            </div>
            <div>
                <DsSearchCombobox
                    helperText="Search by product name"
                    label="With helper text"
                    placeholder="Search product"
                    :size
                />
            </div>
            <div>
                <DsSearchCombobox
                    label="With info icon"
                    labelTooltip="Info icon tooltip"
                    placeholder="Search product"
                    :size
                />
            </div>
            <div>
                <DsSearchCombobox
                    isSearching
                    label="Loading"
                    placeholder="Search product"
                    :size
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { DsSearchComboboxOption } from '@/components/design-system/form/input/DsSearchCombobox/search-combobox.types';
import DsRadio from '@ds/form/input/DsRadio.vue';
import DsSearchCombobox from '@ds/form/input/DsSearchCombobox/DsSearchCombobox.vue';
import type { DsInputSize } from '@ds/form/input/text/text-input.types';
import { useFetch } from '@vueuse/core';
import { ref } from 'vue';

const size = ref<DsInputSize>('md');

interface Product {
    id: string;
    title: string;
    category: string;
}

type ProductOption = DsSearchComboboxOption<Product>;

const defaultOptions: ProductOption[] = [
    {
        label: 'Product 1',
        value: {
            id: '1',
            title: 'Product 1',
            category: 'Category 1',
        },
    },
    {
        label: 'Product 2',
        value: {
            id: '2',
            title: 'Product 2',
            category: 'Category 2',
        },
    },
];

const getSearchResults = async (searchTerm: string): Promise<ProductOption[]> => {
    const params = new URLSearchParams({ q: searchTerm });

    const { data } = await useFetch(`https://dummyjson.com/products/search?${params}`).json<{
        products: Product[];
    }>();

    if (!data.value) return [];

    return data.value.products.map(product => ({
        label: product.title,
        value: {
            id: product.id,
            title: product.title,
            category: product.category,
        },
        groupLabel: product.category,
    }));
};

const selectedProduct1 = ref<Product>();
const productOptions1 = ref<ProductOption[]>();
const isSearchingProduct1 = ref(false);

const handlePerformSearch1 = async (searchTerm: string) => {
    isSearchingProduct1.value = true;
    productOptions1.value = await getSearchResults(searchTerm);
    isSearchingProduct1.value = false;
};

const selectedProduct2 = ref<Product>();
const productOptions2 = ref<ProductOption[]>();
const isSearchingProduct2 = ref(false);

const handlePerformSearch2 = async (searchTerm: string) => {
    isSearchingProduct2.value = true;
    productOptions2.value = await getSearchResults(searchTerm);
    isSearchingProduct2.value = false;
};

const selectedProduct3 = ref<Product>({
    id: '3',
    title: 'Product 3',
    category: 'Category 3',
});
const productOptions3 = ref<ProductOption[]>();
const isSearchingProduct3 = ref(false);

const handlePerformSearch3 = async (searchTerm: string) => {
    isSearchingProduct3.value = true;
    productOptions3.value = await getSearchResults(searchTerm);
    isSearchingProduct3.value = false;
};
</script>
